import { DOMNode, domToReact, Element } from "html-react-parser";
import React from "react";
import UserIconAndName from "~/components/modules/user/UserIconAndName";
import AmpUserReactionButtons from "~/components/modules/userReactionButtons/AmpUserReactionButtons";
import UserReactionButtons, {
  IncreaseUserReactionProps,
} from "~/components/modules/userReactionButtons/UserReactionButtons";
import { QuestionAnswer, UserReactionEnum } from "~/models/questionAnswer";
import { Topic } from "~/models/topic";
import { User } from "~/models/user";

export function replaceQuestionAnswerHtml({
  author,
  domNode,
  getIncreaseUserReactionUrl,
  host,
  increaseUserReactionCallback,
  isAmp,
  questionAnswerId,
  topicSlug,
  userReactionMap = [],
}: {
  author?: User;
  domNode: DOMNode;
  getIncreaseUserReactionUrl?: (data: IncreaseUserReactionProps) => string;
  host?: string;
  increaseUserReactionCallback?: (
    data: IncreaseUserReactionProps
  ) => Promise<void>;
  isAmp: boolean;
  questionAnswerId: QuestionAnswer["id"];
  topicSlug: Topic["slug"];
  userReactionMap: QuestionAnswer["userReactionMap"];
}) {
  if (
    isElement(domNode) &&
    domNode.name === "section" &&
    domNode.attribs.id?.includes("section-")
  ) {
    const sectionId = domNode.attribs.id.replace("section-", "");
    const reaction = userReactionMap?.find((u) => u.sectionId === sectionId);
    const sectionSmileCount = reaction?.smile ?? 0;
    const sectionThumbsUpCount = reaction?.thumbsUp ?? 0;
    return (
      <section id={domNode.attribs.id}>
        {author
          ? domToReact(domNode.children, {
              replace: (domNode: DOMNode) =>
                replaceAuthor({ author, domNode, host }),
            })
          : domToReact(domNode.children)}
        {isAmp ? (
          <AmpUserReactionButtons
            increaseSmileReactionUrl={getIncreaseUserReactionUrl?.({
              questionAnswerId,
              sectionId,
              userReaction: UserReactionEnum.smile,
            })}
            increaseThumbsUpReactionUrl={getIncreaseUserReactionUrl?.({
              questionAnswerId,
              sectionId,
              userReaction: UserReactionEnum.thumbsUp,
            })}
            questionAnswerId={questionAnswerId}
            sectionId={sectionId}
            sectionSmileCount={sectionSmileCount}
            sectionThumbsUpCount={sectionThumbsUpCount}
            topicSlug={topicSlug}
          />
        ) : (
          <UserReactionButtons
            increaseUserReactionCallback={increaseUserReactionCallback}
            questionAnswerId={questionAnswerId}
            sectionId={sectionId}
            sectionSmileCount={sectionSmileCount}
            sectionThumbsUpCount={sectionThumbsUpCount}
            topicSlug={topicSlug}
          />
        )}
      </section>
    );
  }
}

function replaceAuthor({
  author,
  domNode,
  host,
}: {
  author: User;
  domNode: DOMNode;
  host?: string;
}) {
  if (
    isElement(domNode) &&
    domNode.name === "div" &&
    domNode.attribs.class?.includes("md__SingleComment")
  ) {
    return (
      <div className="md__SingleComment">
        <UserIconAndName user={author} host={host} />
        {domToReact([domNode.children[1]])}
      </div>
    );
  }
}

// domNode instanceof Element always returns false after upgrading node packages in https://github.com/apexnap/kikushiru/pull/1077.
// Downgrading only html-react-parser does not solve the problem.
// Reference: https://github.com/remarkablemark/html-react-parser/issues/221#issuecomment-784073240
const isElement = (domNode: DOMNode): domNode is Element => {
  const isTag = domNode.type === "tag";
  const hasAttributes = (domNode as Element).attribs !== undefined;
  return isTag && hasAttributes;
};
